import API from "@/api";
import { setUser, captureException } from "@sentry/browser";

import addressMixin from "@/mixins/addressMixin";

const moment = require("moment");

const CUSTOMER_TYPES = ["Physical", "Legal"];
const SET_USER = "SET_USER";
const SET_PROFILE_PICTURE = "SET_PROFILE_PICTURE";
const SET_OLD_USER = "SET_OLD_USER";
const SET_CLOSED_CITIES = "SET_CLOSED_CITIES";
const SET_SHOW_EMAIL_NOT_VERIFIED_DIALOG = "SET_SHOW_EMAIL_NOT_VERIFIED_DIALOG";
const SET_SET_INTERVAL = "SET_SET_INTERVAL";

export default {
  namespaced: true,

  state: {
    old_user: {
      type: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      cpf: "",
      cnpj: ""
    },
    user: null,
    closedCities: [],
    showEmailNotVerifiedDialog: false,
    setInterval: false
  },

  actions: {
    decodeToken({ commit }, token) {
      let base64Url = token.split(".")[1];
      let base64 = base64Url.replace("-", "+").replace("_", "/");
      return JSON.parse(window.atob(base64));
    },

    saveAccountableOnLocalStorage(context, accountable) {
      localStorage.accountable = JSON.stringify({
        id: accountable.id,
        type: accountable.type
      });
    },

    async refreshUser({ dispatch, state }) {
      await dispatch("getUser", state.user.id);
    },

    async getUser({ dispatch, commit }, accountID) {
      try {
        const user = await API.User.searchByAccountId(accountID);
        dispatch("saveAccountableOnLocalStorage", user.accountable);
        dispatch("setClosedCities");
        commit("SET_USER", user);
      } catch (e) {
        setUser({ email: state.user.email });
        captureException(e);

        dispatch("toast/setToast", { type: "error" }, { root: true });
      }
    },

    async authentication({ dispatch }, creds) {
      try {
        let {
          data: {
            data: { token, account }
          }
        } = await API.Authentication.authenticate(creds);

        const isCustomer = CUSTOMER_TYPES.includes(account.accountable_type);

        if (isCustomer) {
          localStorage.authentication = token;
          await dispatch("getUser", account.id);
        } else {
          throw "Você não tem acesso à esta aplicação";
        }
      } catch (e) {
        if (e.response.status === 404)
          return await dispatch("authenticationOldAPI", creds);

        throw "Usuário ou senha incorretos";
      }
    },

    async authenticationOldAPI({ commit }, creds) {
      try {
        let response = await API.Authentication.authenticateOldAPI(creds);
        commit("SET_OLD_USER", response.user);
      } catch (e) {
        throw "Usuário ou senha incorretos";
      }
    },

    async isAuthenticated({ dispatch, state }, token) {
      try {
        if (token && token.auth) {
          localStorage.authentication = token.auth;
        }

        let authentication = localStorage.authentication;

        if (!authentication && !state.user) throw "Não autorizado";

        let token_data = await dispatch("decodeToken", authentication);
        let expires_at = moment(token_data.exp * 1000);

        if (expires_at < moment()) {
          let checkToken = await API.Authentication.checkToken();
          if (!checkToken) throw "Não autorizado";
        }

        if (!state.user) await dispatch("getUser", token_data.user_id);
        return true;
      } catch (e) {
        dispatch("logout");
        return false;
      }
    },

    clearLocalStorage() {
      localStorage.removeItem("authentication");
      localStorage.removeItem("accountable");
    },

    logout({ commit, dispatch }) {
      commit("newOrder/RESET_STATE", null, {
        root: true
      });
      dispatch("clearLocalStorage");
      commit("SET_USER", null);
    },

    async uploadProfilePicture({ commit, state }, profilePicture) {
      try {
        let profile_picture = await API.Cloudinary.upload(profilePicture);
        await API.User.updateProfilePicture(profile_picture);
        commit("SET_PROFILE_PICTURE", profile_picture);
      } catch (e) {
        throw e;
      }
    },

    async updatePersonalData({ dispatch, state }, personalData) {
      try {
        const accountData = {
          first_name: personalData.first_name,
          last_name: personalData.last_name
        };
        const profileData = {
          city_name: personalData.city_name,
          state_name: personalData.state_name
        };
        const phoneData = personalData.phone;

        await dispatch("updateAccount", accountData);
        await dispatch("updateProfile", profileData);
        await dispatch("updatePhone", phoneData);
        await dispatch("getUser", state.user.id);
      } catch (e) {
        throw e;
      }
    },

    async updateAccount({ state }, account) {
      try {
        await API.User.updateAccount(account);
      } catch (e) {
        throw e;
      }
    },

    async updateProfile({ state }, profile) {
      try {
        await API.User.updateProfile(profile);
      } catch (e) {
        throw e;
      }
    },

    async updatePhone({ state }, phone) {
      try {
        await API.User.updatePhone(phone);
      } catch (e) {
        throw e;
      }
    },

    async createPaymentGateways({
      rootState,
      state,
      dispatch,
      rootGetters,
      commit
    }) {
      try {
        let user = state.user;
        let type =
          user.accountable.type.charAt(0).toUpperCase() +
          user.accountable.type.slice(1);

        user.address = rootGetters["orderScheduler/address"];

        if (!user.payment_gateway_account) {
          await API[type].createPaymentGatewayAccount(user);
        }

        await API[type].createPaymentGatewayCustomer(user);
        await API[type].createOnGateway(user.id);

        dispatch(
          "toast/setToast",
          {
            message:
              "Como essa é a sua primeira limpeza, estamos finalizando algumas verificações...",
            type: "info"
          },
          { root: true }
        );

        const manualRefresh = setInterval(function() {
          dispatch("refreshUser");
        }, 7500);

        commit("SET_SET_INTERVAL", manualRefresh);
        return true;
      } catch (e) {
        setUser({ email: state.user.email });
        captureException(e);

        dispatch(
          "toast/setToast",
          {
            message:
              "Ocorreu um erro inesperado (CODE PGA_PGC), contate nosso suporte!.",
            type: "error"
          },
          { root: true }
        );

        return false;
      }
    },

    async sendEmailVerification({ state, commit }) {
      try {
        await API.User.sendEmailVerification();

        let user = state.user;
        user.status = "waiting_email_verification";
        commit("SET_USER", user);
      } catch (e) {
        throw e;
      }
    },

    async verifyEmail({ commit }, token) {
      try {
        await API.User.verifyEmail(token);
      } catch (e) {
        throw e;
      }
    },

    async sendPasswordRecovery({ state }, email) {
      try {
        await API.User.sendPasswordRecovery(email);
      } catch (e) {
        throw e;
      }
    },

    async sendPasswordReset({ state }, payload) {
      try {
        await API.User.sendPasswordReset(payload);
      } catch (e) {
        throw e;
      }
    },

    async checksForClosedCitiesExistence() {
      try {
        let cities = await API.Addresses.getCities();
        cities = cities.filter(city =>
          addressMixin.methods.isCityClosed(city.attributes)
        );
        return cities.map(city => city.attributes.display_name);
      } catch (e) {
        throw e;
      }
    },

    async setClosedCities({ dispatch, commit }, payload) {
      try {
        let closedCities = await dispatch("checksForClosedCitiesExistence");
        commit("SET_CLOSED_CITIES", closedCities);
      } catch (e) {
        throw e;
      }
    },

    showEmailNotVerifiedDialog({ commit }) {
      commit(SET_SHOW_EMAIL_NOT_VERIFIED_DIALOG, true);
    },

    closeEmailNotVerifiedDialog({ commit }) {
      commit(SET_SHOW_EMAIL_NOT_VERIFIED_DIALOG, false);
    },

    async checkEmail({ state, dispatch }, email) {
      try {
        await API.Accounts.verifyEmail(email);

        dispatch(
          "toast/setToast",
          {
            message: "O e-mail foi confirmado, bora limpar a casa :)",
            type: "success"
          },
          { root: true }
        );

        return true;
      } catch (e) {
        setUser({ email: state.user.email });
        captureException(e);

        dispatch(
          "toast/setToast",
          {
            message: "Ocorreu um erro durante a confirmação do email.",
            type: "error"
          },
          { root: true }
        );

        return false;
      }
    },

    async getCreditCards({ state }) {
      try {
        if (state.user.accountable_type === "Physical") {
          return await API.Physicals.getStoreCreditCards(
            state.user.accountable.id
          );
        } else {
          return await API.Legals.getStoreCreditCards(
            state.user.accountable.id
          );
        }
      } catch (e) {
        throw "Não foi possível buscar os seus cartões de crédito salvos";
      }
    },
    async clearSetInterval({ state, commit }) {
      try {
        if (state.setInterval) {
          clearInterval(state.setInterval);

          commit("SET_SET_INTERVAL", null);
        }
      } catch (e) {}
    }
  },

  mutations: {
    [SET_CLOSED_CITIES](state, payload) {
      state.closedCities = payload;
    },
    [SET_USER](state, payload) {
      state.user = payload;
    },
    [SET_PROFILE_PICTURE](state, payload) {
      state.user.profile.profile_picture = payload;
    },
    [SET_OLD_USER](state, payload) {
      state.old_user.first_name = payload.first_name ? payload.first_name : "";
      state.old_user.last_name = payload.last_name ? payload.last_name : "";
      state.old_user.email = payload.email ? payload.email : "";
      state.old_user.phone = payload.phone
        ? payload.phone.area_code + payload.phone.number
        : "";

      if (payload.cnpj) {
        state.old_user.type = "legal";
        state.old_user.cnpj = payload.cnpj;
      } else if (payload.cpf) {
        state.old_user.type = "physical";
        state.old_user.cpf = payload.cpf;
      }
    },
    [SET_SHOW_EMAIL_NOT_VERIFIED_DIALOG](state, payload) {
      state.showEmailNotVerifiedDialog = payload;
    },
    [SET_SET_INTERVAL](state, setIntervalIdentifier) {
      state.setInterval = setIntervalIdentifier;
    }
  },

  getters: {
    userType: state => {
      return state.user.accountable.type;
    },

    userAccountableId: state => {
      return state.user ? state.user.accountable.id : "";
    },

    email: state => {
      return state.user ? state.user.email : "";
    },

    fbc: state => {
      return document.cookie.includes("_fbc")
        ? document.cookie.split("_fbc=")[1].split(";")[0]
        : "";
    },

    fbp: state => {
      return document.cookie.includes("_fbp")
        ? document.cookie.split("_fbp=")[1].split(";")[0]
        : "";
    },

    hasNoVerifiedEmail(state) {
      return state.user && state.user.status === "waiting_email_verification";
    },
    update_your_data(state) {
      return state.old_user.email != "";
    },

    full_name(state) {
      return state.user
        ? state.user.first_name + " " + state.user.last_name
        : "";
    },

    firstName: state => {
      if (state.user && state.user.first_name) {
        return (
          state.user.first_name[0].toUpperCase() +
          state.user.first_name.slice(1).toLowerCase()
        );
      }
    },

    phone_number(state) {
      return state.user && state.user.phones && state.user.phones.length
        ? `(${state.user.phones[0].area_code}) ${state.user.phones[0].number}`
        : "";
    },

    phone_number_fb: state => {
      return state.user && state.user.phones && state.user.phones.length
        ? `${state.user.phones[0].country_code}${
            state.user.phones[0].area_code
          }${state.user.phones[0].number}`
        : "";
    },

    birthdate(state) {
      return state.user
        ? moment(state.user.profile.birthdate).format("DD/MM/Y")
        : "";
    },

    ddd(state) {
      return state.user && state.user.phones && state.user.phones.length
        ? state.user.phones[0].area_code
        : "";
    },

    cpf(state) {
      return state.user && state.user.accountable.type === "physicals"
        ? state.user.accountable.cpf
        : "";
    },

    cnpj(state) {
      return state.user && state.user.accountable.type === "legals"
        ? state.user.accountable.cpf
        : "";
    },

    answeredTermsOfUse(state) {
      return (
        "accounts_terms_of_uses" in state.user &&
        state.user.accounts_terms_of_uses.length > 0 &&
        state.user.accounts_terms_of_uses[0].accept
      );
    },

    userStatus: state => {
      return state.user.status;
    },

    firstOrderUser: state => {
      return ["email_verified", "waiting_email_verification"].includes(
        state.user.status
      );
    },
    accountBalanceAvailable: state => {
      return state.user.payment_gateway_account.wallet.available_to_use;
    },
    accountBonusAvailable: state => {
      return state.user.payment_gateway_account.wallet.bonus;
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/user.js